import { useState } from "react";
import { motion } from "framer-motion";
import { Heart, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const PRESETS = [10, 20, 50, 100];

const inputCls =
  "w-full bg-clay-bg border border-clay-border focus:border-clay-orange outline-none px-4 py-3 text-clay-text placeholder:text-clay-muted/60 transition-colors duration-300";

export default function Donate() {
  const [amount, setAmount] = useState(20);
  const [custom, setCustom] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState(false);

  const finalAmount = custom !== "" ? parseFloat(custom) : amount;
  const valid = finalAmount >= 1 && name.trim() && /.+@.+\..+/.test(email);

  const fieldCls = (ok) =>
    touched && !ok ? `${inputCls} border-red-500` : inputCls;

  const donate = async () => {
    setTouched(true);
    if (!valid) {
      toast.error(
        !name.trim()
          ? "Please enter your name"
          : !/.+@.+\..+/.test(email)
          ? "Please enter a valid email so we can send your receipt"
          : "Choose an amount of $1 or more"
      );
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/donations", {
        amount: finalAmount,
        name,
        email,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setLoading(false);
    }
  };

  return (
    <section id="donate" className="border-y border-clay-border bg-clay-surface/40">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          className="lg:col-span-6"
        >
          <p className="font-mono text-xs uppercase tracking-[0.35em] text-clay-orange mb-4">Chip In</p>
          <h2 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-5xl lg:text-6xl leading-[0.95] mb-6">
            Keep Darlington <span className="text-clay-orange">Racing.</span>
          </h2>
          <p className="text-clay-muted text-lg leading-relaxed max-w-xl">
            Not ready for a membership or sponsorship? A one-off donation still makes a real difference — fuel for the
            water truck, safety gear for volunteers, lights over the clay. Every dollar stays at the track.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ delay: 0.12 }}
          className="lg:col-span-6 border border-clay-border bg-clay-card p-6 sm:p-8"
        >
          <div className="grid grid-cols-4 gap-2 mb-4">
            {PRESETS.map((p) => (
              <button
                key={p}
                data-testid={`donate-preset-${p}`}
                type="button"
                onClick={() => { setAmount(p); setCustom(""); }}
                className={`font-display font-black text-2xl py-4 border transition-all duration-300 ${
                  custom === "" && amount === p
                    ? "bg-clay-orange text-clay-bg border-clay-orange clay-glow"
                    : "border-clay-border text-clay-muted hover:border-clay-orange/60 hover:text-clay-text"
                }`}
              >
                ${p}
              </button>
            ))}
          </div>
          <div className="relative mb-4">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 font-display font-bold text-clay-muted text-lg">$</span>
            <input
              data-testid="donate-custom-input"
              type="number"
              min="1"
              step="1"
              placeholder="Or any amount — your call"
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              className={`${inputCls} pl-9`}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
            <input data-testid="donate-name-input" placeholder="Name *" value={name} onChange={(e) => setName(e.target.value)} className={fieldCls(!!name.trim())} required />
            <input data-testid="donate-email-input" type="email" placeholder="Email for receipt *" value={email} onChange={(e) => setEmail(e.target.value)} className={fieldCls(/.+@.+\..+/.test(email))} required />
          </div>
          <button
            data-testid="donate-submit-button"
            onClick={donate}
            disabled={loading}
            className="w-full bg-clay-orange hover:bg-clay-gold disabled:opacity-40 disabled:cursor-not-allowed text-clay-bg font-display font-bold uppercase tracking-wider text-lg px-8 py-4 transition-all duration-300 flex items-center justify-center gap-2 clay-glow"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Heart className="w-5 h-5" />}
            {loading ? "Redirecting…" : `Donate $${(finalAmount >= 1 ? finalAmount : 0).toLocaleString()}`}
          </button>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay-muted text-center mt-3">
            Name & email required so we can send your receipt — secure one-off payment
          </p>
        </motion.div>
      </div>
    </section>
  );
}
